/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  GamoraErrorNotImplemented,
  GamoraFoundationModule,
  assert,
  debugCreate,
} from '@ver.id/libs.internal.gamora';
import {
  HoruzProtocolChulakDataType,
  HoruzProtocolChulakDataVersion,
} from '../data.js';
import {
  HoruzProtocolChulakMessage,
  HoruzProtocolChulakMessageAgentStartCompletedGuard,
  HoruzProtocolChulakMessageAgentStartEnterGuard,
  HoruzProtocolChulakMessageAgentStartExitGuard,
  HoruzProtocolChulakMessageAppPairingExitGuard,
  HoruzProtocolChulakMessageAppPresentationExitGuard,
  HoruzProtocolChulakMessageSessionAbortedGuard,
} from '../guards/message/index.js';
import { HoruzProtocolChulakErrorProtocolInvalidMessage } from '../errors/index.js';
import { HoruzProtocolChulakMessageAppPairingCompletedGuard } from '../guards/message/app-pairing-completed.js';
import { HoruzProtocolChulakMessageAppPairingEnterGuard } from '../guards/message/app-pairing-enter.js';
import { HoruzProtocolChulakMessageAppPresentationCompletedGuard } from '../guards/message/app-presentation-completed.js';
import { HoruzProtocolChulakMessageAppPresentationEnterGuard } from '../guards/message/app-presentation-enter.js';
import { HoruzProtocolChulakMessageAppTagsRefreshedGuard } from '../guards/message/app-tags-refreshed.js';
import { HoruzProtocolChulakMessageAuthCompletedGuard } from '../guards/message/auth-completed.js';
import { HoruzProtocolChulakMessageAuthGuard } from '../guards/message/auth.js';
import { HoruzProtocolChulakMessageGuard } from '../guards/message/base.js';
const debug = debugCreate({ namespace: 'horuz:protocols:chulak:module:data' });

/**
 * @public
 */
export class HoruzProtocolChulakMessageModule extends GamoraFoundationModule {
  /**
   * Creates message
   *
   * Protocol layout
   *
   * |----------------------------------------------|
   * | Version (uint32) | Type (uint32) | Base64URL |
   * |----------------------------------------------|
   *
   * @param message The message
   * @returns data
   */
  static Encode<TType extends HoruzProtocolChulakDataType>(
    message: HoruzProtocolChulakMessageGuard<TType>,
  ): Uint8Array<ArrayBuffer> {
    assert(
      typeof message === 'object' && message !== null,
      'Invalid message argument: not a valid object.',
      HoruzProtocolChulakErrorProtocolInvalidMessage,
    );
    assert(
      typeof message.type === 'number' && Number.isSafeInteger(message.type),
      'Invalid message.type: not a valid number.',
      HoruzProtocolChulakErrorProtocolInvalidMessage,
    );
    assert(
      message instanceof HoruzProtocolChulakMessageGuard,
      'Invalid message.guard: not a valid guard.',
      HoruzProtocolChulakErrorProtocolInvalidMessage,
    );

    const encoder = new TextEncoder();
    const version = HoruzProtocolChulakDataVersion;

    // Encode message
    const args = encoder.encode(message.toBase64URL());
    const buffer = new ArrayBuffer(4 * 2 + args.length);
    const bufferView = new Uint8Array(buffer);
    const bufferDataView = new DataView(buffer);

    // Write to buffer
    bufferDataView.setUint32(0, version);
    bufferDataView.setUint32(4, message.type);
    bufferView.set(args, 2 * 4);

    // Returns buffer
    return bufferView;
  }

  /**
   * Parses raw JSON
   * @param type The type
   * @param input The JSON input
   */
  static Parse<TType extends HoruzProtocolChulakDataType>(
    type: TType,
    inputBase64URL: unknown,
  ) {
    // Validate buffer
    switch (type) {
      case HoruzProtocolChulakDataType.AUTH: {
        return HoruzProtocolChulakMessageAuthGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.AUTH_COMPLETED: {
        return HoruzProtocolChulakMessageAuthCompletedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_APP_PAIRING_ENTER: {
        return HoruzProtocolChulakMessageAppPairingEnterGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_APP_PAIRING_EXIT: {
        return HoruzProtocolChulakMessageAppPairingExitGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_AGENT_START_ENTER: {
        return HoruzProtocolChulakMessageAgentStartEnterGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_AGENT_START_EXIT: {
        return HoruzProtocolChulakMessageAgentStartExitGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_APP_PRESENTATION_ENTER: {
        return HoruzProtocolChulakMessageAppPresentationEnterGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.STATE_APP_PRESENTATION_EXIT: {
        return HoruzProtocolChulakMessageAppPresentationExitGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.DATA_APP_PAIRING_COMPLETED: {
        return HoruzProtocolChulakMessageAppPairingCompletedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.DATA_AGENT_START_COMPLETED: {
        return HoruzProtocolChulakMessageAgentStartCompletedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.DATA_APP_TAGS_REFRESHED: {
        return HoruzProtocolChulakMessageAppTagsRefreshedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.DATA_APP_PRESENTATION_COMPLETED: {
        return HoruzProtocolChulakMessageAppPresentationCompletedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      case HoruzProtocolChulakDataType.DATA_SESSION_ABORTED: {
        return HoruzProtocolChulakMessageSessionAbortedGuard.fromBase64URL(
          inputBase64URL,
        );
      }
      default: {
        throw new GamoraErrorNotImplemented();
      }
    }
  }
  /**
   * Parses protocol message
   *
   * Protocol layout
   *
   * |----------------------------------------------|
   * | Version (uint32) | Type (uint32) | Base64URL |
   * |----------------------------------------------|
   *
   * @note Throws
   * @param data The message
   */
  static Decode<T extends HoruzProtocolChulakMessage>(buffer: Uint8Array): T {
    assert(
      buffer instanceof Uint8Array,
      'Invalid buffer argument: not a valid Uint8Array.',
      HoruzProtocolChulakErrorProtocolInvalidMessage,
    );
    assert(
      buffer.byteLength >= 2 * 4 + 1,
      'Invalid buffer argument: not valid Uint8Array',
      HoruzProtocolChulakErrorProtocolInvalidMessage,
    );

    try {
      // Get a view
      const bufferDataView = new DataView(
        buffer.buffer,
        buffer.byteOffset,
        buffer.byteLength,
      );
      const bufferEncodedDataArray = new Uint8Array(
        buffer.buffer,
        buffer.byteOffset + 2 * 4,
        buffer.byteLength - 2 * 4,
      );
      const decoder = new TextDecoder();

      // Protocol encoding
      const version = bufferDataView.getUint32(0);
      const type = bufferDataView.getUint32(4);

      // Ensure protocol support
      // @TODO convert to semver
      assert(
        version <= HoruzProtocolChulakDataVersion,
        'Invalid version: ' +
          version +
          ' is an unsupported chulak protocol version.',
      );

      // Parse buffer
      const base64URLInput = decoder.decode(bufferEncodedDataArray);

      // Parse
      return this.Parse(type, base64URLInput) as T;
    } catch (e: any) {
      debug.trace(e?.message, e?.stack);
      throw HoruzProtocolChulakErrorProtocolInvalidMessage.Create(
        'Unable to decode message.',
      );
    }
  }
}
